import React from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import {
  receiptDraftRemoved,
  receiptRemoved,
  receiptSentBack,
  receiptSnoozed,
  receiptUpdated,
} from "store/actions/receiptActions";
//#endregion
//#region types
import { ACTION_SEPARATOR, MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ReceiptMoreActionProps } from "models/propTypes/receiptPropTypes";
import { StyleEnums, DialogEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { DialogProps } from "models/propTypes/dialogPropTypes";
//#endregion
//#region components
import ReceiptSendBackFormContainer from "./forms/receiptSendBackFormContainer";
import useReceiptDownload from "./useDownload";
import useReceiptTodoHelpers from "./useTodoHelpers";
import { MoreActions, useCheckRelations, useSnoozeAction } from "components/common";
import { useReceiptService } from "serviceHooks";
import useReceiptPermissions from "./usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const ReceiptMoreActions: React.FC<ReceiptMoreActionProps> = (props) => {
  const receiptService = useReceiptService();
  const { receipt, reloadReceipt, markAsDone, disableSuccessMessages } = props;
  const loggedInUser = useSelector((state: AppState) => state.auth.loggedInUser);
  const dispatch = useDispatch();
  const { updateReceiptTodo } = useReceiptTodoHelpers();

  const { getIsReadonlyUser } = useCheckRelations();
  const { getReceiptAdminPermissions } = useReceiptPermissions();

  //#region send back
  const sendBackSuccess = () => {
    reloadReceipt((r) =>
      updateReceiptTodo(
        r,
        (r) => dispatch(receiptUpdated(r)),
        () => dispatch(receiptSentBack(receipt.id))
      )
    );
  };

  const sendBackAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SendBack),
    icon: IconDefinitionConstants.WSendBack,
    actionDialog: {
      isOpen: false,
      title: `${LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Common.Return
      )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipt2)}`,
      subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptReturnMessage),
      titleIcon: IconDefinitionConstants.WSendBack,
      dialogType: DialogEnums.DialogType.Content,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Return),
          successMessage: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Receipt.SendBackSuccessMessage
          ),
        },
      ],
      children: <ReceiptSendBackFormContainer receiptId={receipt.id} />,
      maxWidth: "sm",
    },
    onActionSuccess: sendBackSuccess,
  };
  //#endregion

  //#region snooze
  const onSnoozeSuccess = () => {
    dispatch(receiptSnoozed(receipt.id));
    markAsDone();
  };

  const { snoozeAction } = useSnoozeAction(receipt.id, WintSuperkollAPIModel.TodoType.Receipt, onSnoozeSuccess);
  //#endregion

  //#region download
  const onDownloadClick = useReceiptDownload(receipt.id);

  const downloadAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Common.Download
    )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipt)}`,
    icon: IconDefinitionConstants.WDownload,
    onClickHandler: onDownloadClick,
  };
  //#endregion

  //#region cancel receipt
  const onClickDelete = () => {
    return receiptService.deleteRecipt(receipt.id);
  };

  const onDeleteSuccess = () => {
    dispatch(receiptRemoved(receipt.id));
    markAsDone();
  };

  const deleteWarningDialog: DialogProps = {
    isOpen: false,
    title: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Common.Delete
    )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipt)}?`,
    titleIcon: IconDefinitionConstants.WTrashAlt,
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Remove),
        onClick: onClickDelete,
        type: StyleEnums.DisplayType.Danger,
        successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.CancelSuccessMessage),
      },
    ],
    dialogType: DialogEnums.DialogType.Message,
    children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DeleteWarningMessage),
    type: StyleEnums.DisplayType.Danger,
    onError: (_?: any, closeDialog?: () => void) => closeDialog && closeDialog(),
  };

  const onWintPaymentDeleteSuccess = () => {
    dispatch(receiptDraftRemoved(receipt.id));
    markAsDone();
  };

  const wintPaymentDeleteWarningDialog: DialogProps = {
    isOpen: false,
    title: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Common.Remove
    )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipt)}?`,
    titleIcon: IconDefinitionConstants.WWarningCanvas,
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.SendInvoice),
        onClick: onClickDelete,
        type: StyleEnums.DisplayType.Danger,
        successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.CancelSuccessMessage),
      },
    ],
    dialogType: DialogEnums.DialogType.Message,
    children: (
      <div>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.WintPaymentRemoveWarning)} <br />
        <br />
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.WintPaymentRemoveWarning2)}
      </div>
    ),
    type: StyleEnums.DisplayType.Danger,
    onError: (_?: any, closeDialog?: () => void) => closeDialog && closeDialog(),
  };

  const cancelReceiptAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
    icon: IconDefinitionConstants.WTrashAlt,
    actionDialog:
      receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard
        ? wintPaymentDeleteWarningDialog
        : deleteWarningDialog,
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess:
      receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard
        ? onWintPaymentDeleteSuccess
        : onDeleteSuccess,
  };
  //#endregion

  const getMenuItems = (): MoreActionMenuItemType[] => {
    const actions: MoreActionMenuItemType[] = [];
    props.costForwardingAction && actions.push(props.costForwardingAction);
    const receiptStatuses = WintSuperkollAPIModel.ReceiptStateApiModel;
    const paymentMethodTypes = WintSuperkollAPIModel.ReceiptPaymentMethodType;
    const isReadonlyUser = getIsReadonlyUser();

    if (!isReadonlyUser) {
      const isReceiptsAdmin = getReceiptAdminPermissions(false);
      const waitingAcceptanceAtAdmin = props.receipt.state === receiptStatuses.WaitingAcceptance && isReceiptsAdmin;

      const wintCardDraft =
        props.receipt.state === receiptStatuses.Draft &&
        props.receipt.paymentMethodType === paymentMethodTypes.WintCard;

      const sentBackToPerson =
        (props.receipt.state === receiptStatuses.SentBackToCustomer ||
          props.receipt.state === receiptStatuses.SentBackFromReceiptAdmin) &&
        (props.receipt.createdByPersonId === loggedInUser.personId || isReceiptsAdmin);

      waitingAcceptanceAtAdmin && actions.push(sendBackAction);
      receipt.attachments?.length && actions.push(downloadAction);

      if (waitingAcceptanceAtAdmin || sentBackToPerson || wintCardDraft) {
        if (waitingAcceptanceAtAdmin || sentBackToPerson) {
          actions.push(snoozeAction);
        }
        actions.length > 0 && actions.push(ACTION_SEPARATOR);
        actions.push(cancelReceiptAction);
      }
    } else {
      receipt.attachments?.length && actions.push(downloadAction);
    }

    return actions;
  };

  return <MoreActions menuItems={getMenuItems()} disableSuccessMessages={disableSuccessMessages} />;
};

export default ReceiptMoreActions;
